/**
 * FlowsDialog.tsx — table → flow-net graph.
 *
 * The one-click surface for the events→substrate conversion (the same
 * machinery the chat `graph_from_table` tool runs): pick the event/label,
 * ordering and session columns of a table, pick the rate formula (the
 * weight IS the formula — presented with the sample in doc 'flows'), and
 * POST /graphs/from-table. The backend lands the graph on the canvas via
 * the graphAdded/graphUpdate broadcast — this dialog only reports the
 * summary (visits, transition counts, the top natural paths).
 */
import { useMemo, useState } from 'react';
import { GraphIcon, X } from '@phosphor-icons/react';
import * as api from './dataApi';

export interface FlowsDialogProps {
  origin: api.DataOrigin;
  /** Table schemas for the column pickers; null while loading. */
  tables: api.TableSummary[] | null;
  /** Preselect this table (the one the user is looking at). */
  initialTable?: string | null;
  onClose: () => void;
}

const WEIGHT_MODES: { value: string; label: string; hint: string }[] = [
  { value: 'count', label: 'count', hint: 'transition frequency' },
  { value: 'delta', label: 'delta', hint: 'mean time between transitions' },
  { value: 'mean:', label: 'mean:column', hint: "mean of the target event's column" },
  { value: 'sum:', label: 'sum:column', hint: "sum of the target event's column" },
];

function isNumeric(t: string): boolean {
  return ['integer', 'float', 'number', 'real'].includes(t);
}

export default function FlowsDialog({
  origin,
  tables,
  initialTable,
  onClose,
}: FlowsDialogProps): JSX.Element {
  const first = initialTable ?? tables?.[0]?.name ?? '';
  const [table, setTable] = useState<string>(first);
  const [label, setLabel] = useState<string>('');
  const [order, setOrder] = useState<string>('');
  const [group, setGroup] = useState<string>('');
  const [mode, setMode] = useState<string>('count');
  const [modeCol, setModeCol] = useState<string>('');
  const [capacity, setCapacity] = useState<string>('');
  const [signal, setSignal] = useState(false);
  const [graphId, setGraphId] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<api.GraphFromTableResult | null>(null);

  const schema = useMemo(
    () => tables?.find((t) => t.name === table)?.columns ?? [],
    [tables, table],
  );
  const textCols = schema.filter((c) => c.name !== 'id' && !isNumeric(c.type)).map((c) => c.name);
  const numCols = schema.filter((c) => c.name !== 'id' && isNumeric(c.type)).map((c) => c.name);
  const allCols = schema.filter((c) => c.name !== 'id').map((c) => c.name);

  // sensible auto-picks whenever the table changes: the first text column
  // as the event label, the first numeric column as the order
  const pickTable = (name: string): void => {
    setTable(name);
    const cols = tables?.find((t) => t.name === name)?.columns ?? [];
    const txt = cols.find((c) => c.name !== 'id' && !isNumeric(c.type))?.name ?? '';
    const num = cols.find((c) => c.name !== 'id' && isNumeric(c.type))?.name ?? '';
    setLabel(txt);
    setOrder(num);
    setGroup('');
    setModeCol('');
    setCapacity('');
    setGraphId(`${name.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}-flows`);
    setResult(null);
    setError(null);
  };
  if (label === '' && textCols.length > 0) setLabel(textCols[0]);
  if (order === '' && numCols.length > 0) setOrder(numCols[0]);

  const weight = mode.endsWith(':') ? `${mode}${modeCol}` : mode;
  const needsCol = mode.endsWith(':');
  const valid =
    table !== '' && label !== '' && order !== '' && (!needsCol || modeCol !== '');

  const submit = async (): Promise<void> => {
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.graphFromTable(origin, {
        table,
        label,
        order,
        group: group || null,
        weight,
        capacity: capacity || null,
        signal,
        graph_id: graphId.trim() || null,
      });
      setResult(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const sel = (value: string, onChange: (v: string) => void, cols: string[], hint: string) => (
    <select className="text-input" value={value} onChange={(e) => onChange(e.target.value)} title={hint}>
      {cols.length === 0 && <option value="">—</option>}
      {cols.map((c) => (
        <option key={c} value={c}>
          {c}
        </option>
      ))}
    </select>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Table → Graph</h2>
          <button type="button" className="btn-icon-mini modal-close" onClick={onClose}>
            <X size={16} />
          </button>
        </div>
        <div className="modal-body">
          <p className="data-muted" style={{ marginTop: 0 }}>
            Events become nodes, consecutive events within a session become edges —
            loops and funnels both fall out. The weight is the rate formula.
          </p>
          <div className="flows-form">
            <label>
              <span>Table</span>
              <select
                className="text-input"
                value={table}
                onChange={(e) => pickTable(e.target.value)}
              >
                {(tables ?? []).map((t) => (
                  <option key={t.name} value={t.name}>
                    {t.name}
                    {typeof t.count === 'number' ? ` (${t.count})` : ''}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Event column (label)</span>
              {sel(label, setLabel, textCols, 'the pages/sections — text column')}
            </label>
            <label>
              <span>Order column</span>
              {sel(order, setOrder, numCols, 'usually a timestamp — numeric column')}
            </label>
            <label>
              <span>Session / user column</span>
              <select
                className="text-input"
                value={group}
                onChange={(e) => setGroup(e.target.value)}
                title="rows sharing a value form one path — strongly recommended"
              >
                <option value="">— (all rows chain)</option>
                {allCols.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Weight (rate formula)</span>
              <select
                className="text-input"
                value={mode}
                onChange={(e) => setMode(e.target.value)}
                title={WEIGHT_MODES.find((m) => m.value === mode)?.hint ?? ''}
              >
                {WEIGHT_MODES.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label} — {m.hint}
                  </option>
                ))}
              </select>
            </label>
            {needsCol && (
              <label>
                <span>…of column</span>
                {sel(modeCol, setModeCol, numCols, 'numeric column')}
              </label>
            )}
            <label>
              <span>Capacity column (optional)</span>
              {sel(capacity, setCapacity, numCols, "per-node max becomes the node's capacity")}
            </label>
            <label>
              <span>Graph id</span>
              <input
                className="text-input"
                type="text"
                value={graphId}
                placeholder={`${table || 'table'}-flows`}
                onChange={(e) => setGraphId(e.target.value)}
              />
            </label>
            <label className="flows-check" title="edges fill the target without draining the source">
              <input type="checkbox" checked={signal} onChange={(e) => setSignal(e.target.checked)} />
              attention graph (non-conserving edges)
            </label>
          </div>

          {result && (
            <div className="flows-result">
              <div>
                <GraphIcon size={14} />{' '}
                <strong>{result.graphId}</strong>
                {result.replaced ? ' (replaced)' : ''} — {result.rows} rows →{' '}
                {result.nodes.length} nodes, {result.edges.length} edges. The graph is
                on the canvas; visits are the initial stocks.
              </div>
              {result.topPaths.length > 0 && (
                <div>
                  top paths:{' '}
                  {result.topPaths.map((p, i) => (
                    <span key={i} className="flows-path">
                      {p.nodes.join(' → ')}{' '}
                      <em>{(p.share * 100).toFixed(1)}%</em>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
          {error && <p className="data-error" style={{ border: 'none', padding: 0 }}>{error}</p>}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn" onClick={onClose}>
            {result ? 'Close' : 'Cancel'}
          </button>
          <button
            type="button"
            className="btn btn-start"
            disabled={!valid || busy}
            onClick={() => void submit()}
          >
            {busy ? 'Converting…' : 'Create Graph'}
          </button>
        </div>
      </div>
    </div>
  );
}
